import React from 'react';
import styled from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import Icon from 'view/Icon';
import { Border, Main, Text, Background } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import {
  AFFILIATE_FORM_LABELS,
  AFFILIATE_FORM_REGIONS,
  AffiliateFormRegion,
  REGIONS
} from 'constants/affiliateForm';

const Layout = styled.div`
  display: flex;
  gap: ${Space.sp3x};
  align-items: stretch;
`;

const Sidebar = styled.div`
  width: 200px;
  flex-shrink: 0;
`;

const SidebarHeading = styled.p`
  margin: 0 0 ${Space.spBase} 0;
  font-size: 14px;
  color: ${Text.primary};
`;

const RegionButton = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  padding: ${Space.spBasePlus} ${Space.sp2x};
  margin-bottom: ${Space.spBase};
  cursor: pointer;
  font-size: 14px;
  text-align: center;
  border-radius: ${BorderRadius.s};
  border: 1px solid ${({ $active }) => ($active ? Border.brand : Border.light)};
  background-color: ${({ $active }) => ($active ? Background.lightBrand : Background.shaded)};
  color: ${({ $active }) => ($active ? Main.brandDark : Text.primary)};

  &:hover {
    border-color: ${Border.brand};
  }
`;

const InfoNote = styled(FlexboxContainer)`
  margin-top: ${Space.sp2x};
  gap: ${Space.spHalf};
  color: ${Text.secondary};
  font-size: 12px;
  line-height: 16px;
`;

const PreviewArea = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${Border.main};
  border-radius: ${BorderRadius.m};
  background-color: ${Background.white};
  /* Keeps the framed form off the container's top and bottom edges. Vertical only — the
     form renders its own horizontal gutters, so side padding would double up. */
  padding: ${Space.sp2x} 0;
  /* AC 7 — the preview scrolls within its own area rather than growing the page. */
  overflow: auto;
`;

/*
 * Covers the frame while it loads. The iframe stays mounted underneath rather than being
 * swapped out for this overlay — an unmounted iframe never fires `load`, which would leave
 * every apply to time out as a failure.
 */
const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Background.white};
  z-index: 1;
`;

const EmptyState = styled.div`
  max-width: 420px;
  padding: ${Space.sp4x};
  text-align: center;
`;

const EmptyTitle = styled.p`
  margin: ${Space.sp2x} 0 ${Space.spBase} 0;
  font-size: 16px;
  font-weight: 600;
  color: ${Text.primary};
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${Text.secondary};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${Space.sp4x};
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${Text.primary};
`;

const FormFrame = styled.iframe<{ $hidden: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 420px;
  border: 0;
  /* visibility (not display:none) so the frame still loads while hidden */
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

export interface Props {
  selectedRegion: AffiliateFormRegion;
  onSelectRegion: (region: AffiliateFormRegion) => void;
  /** URL to render for the selected region, or null when that region has no form. */
  previewUrl: string | null;
  isLoading: boolean;
  /**
   * Optional, and normally left unset. A frame `load` says nothing about whether the form is
   * real — Cognito's "Form not found!" page loads just as happily — so it must not be used to
   * decide an apply. Validation lives in the existence check in AffiliateFormView.
   */
  onLoad?: () => void;
  onError?: () => void;
}

const CheckoutFormPreview = ({
  selectedRegion,
  onSelectRegion,
  previewUrl,
  isLoading,
  onLoad,
  onError
}: Props) => {
  const renderPreview = () => (
    <>
      {previewUrl && (
        <FormFrame
          // Re-key on the URL so switching region or replacing a form forces a real reload
          // (and therefore a fresh load/error event) rather than reusing the old document.
          key={previewUrl}
          title={`${AFFILIATE_FORM_LABELS.PREVIEW_TITLE} ${REGIONS[selectedRegion].label}`}
          src={previewUrl}
          onLoad={onLoad}
          onError={onError}
          allow="payment"
          $hidden={isLoading}
        />
      )}

      {isLoading && (
        <LoadingOverlay>
          <LoadingState>
            <LoadingIndicator size={40} margin={`0 auto ${Space.sp2x} auto`} />
            <LoadingText>{AFFILIATE_FORM_LABELS.PREVIEW_LOADING}</LoadingText>
          </LoadingState>
        </LoadingOverlay>
      )}

      {!previewUrl && !isLoading && (
        <EmptyState>
          <Icon name="file-text" size={48} fillColor={Text.disabled} />
          <EmptyTitle>{AFFILIATE_FORM_LABELS.EMPTY_TITLE}</EmptyTitle>
          <EmptyDescription>{AFFILIATE_FORM_LABELS.EMPTY_DESCRIPTION}</EmptyDescription>
        </EmptyState>
      )}
    </>
  );

  return (
    <Layout>
      <Sidebar>
        <SidebarHeading>{AFFILIATE_FORM_LABELS.PREVIEW_SELECT_REGION}</SidebarHeading>
        {AFFILIATE_FORM_REGIONS.map(region => (
          <RegionButton
            key={region}
            type="button"
            $active={region === selectedRegion}
            onClick={() => onSelectRegion(region)}
          >
            {REGIONS[region].label}
          </RegionButton>
        ))}
        <InfoNote alignItems="flex-start">
          <Icon name="info" size={14} fillColor={Text.secondary} />
          <span>{AFFILIATE_FORM_LABELS.PREVIEW_INFO}</span>
        </InfoNote>
      </Sidebar>
      <PreviewArea>{renderPreview()}</PreviewArea>
    </Layout>
  );
};

export default CheckoutFormPreview;
